import React from 'react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="policy-container privacy-policy">
      <div className="policy-header">
        <h1>🔒 Privacy Policy & POPIA Compliance</h1>
        <p className="last-updated">Last Updated: January 8, 2026</p>
        <p className="popia-badge">South African POPIA Compliant</p>
      </div>

      <div className="policy-content">
        <section className="policy-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Heritage Hub (hereinafter "we," "us," "our," or "Company"). 
            We are committed to protecting your privacy and ensuring you have a positive experience on our website.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
            visit our website and use our services, in compliance with the Protection of Personal Information Act, 
            2013 (POPIA) and other applicable South African privacy laws.
          </p>
          <p>
            Please read this Privacy Policy carefully. If you do not agree with our policies and practices, 
            please do not use our website.
          </p>
        </section>

        <section className="policy-section">
          <h2>2. What Personal Information Do We Collect?</h2>
          
          <div className="privacy-subsection">
            <h3>2.1 Information You Provide Directly</h3>
            <p>
              We collect information you voluntarily provide when you:
            </p>
            <ul>
              <li><strong>Register an Account:</strong> Name, email address, phone number, address, and password</li>
              <li><strong>Make a Purchase:</strong> Billing address, shipping address, payment information, and order history</li>
              <li><strong>Apply as a Vendor:</strong> Business details, contact information, tax number, product information, and uploaded documents</li>
              <li><strong>Contact Us:</strong> Name, email, phone number, and message content</li>
              <li><strong>Submit Forms:</strong> Information requested in any forms on our website</li>
              <li><strong>Participate in Surveys:</strong> Your responses and feedback</li>
              <li><strong>Subscribe to Services:</strong> Email address and preferences</li>
            </ul>
          </div>

          <div className="privacy-subsection">
            <h3>2.2 Information Collected Automatically</h3>
            <p>
              When you visit our website, we automatically collect certain information:
            </p>
            <ul>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address, and device identifiers</li>
              <li><strong>Usage Information:</strong> Pages visited, time spent on pages, links clicked, search queries, and referring/exit pages</li>
              <li><strong>Location Information:</strong> General geographic location based on IP address (not precise GPS)</li>
              <li><strong>Cookies and Tracking Data:</strong> As described in our Cookie Policy</li>
              <li><strong>Performance Data:</strong> Website speed, errors encountered, and performance metrics</li>
            </ul>
          </div>

          <div className="privacy-subsection">
            <h3>2.3 Information from Third Parties</h3>
            <p>
              We may receive information about you from:
            </p>
            <ul>
              <li><strong>Payment Processors:</strong> Stripe and PayPal may share transaction and verification data</li>
              <li><strong>Social Media:</strong> If you link your social media accounts or use social login features</li>
              <li><strong>Business Partners:</strong> Shipping providers, email service providers, and marketing partners</li>
              <li><strong>Public Sources:</strong> Publicly available information about vendors for verification purposes</li>
              <li><strong>Other Users:</strong> Information you may be mentioned in by other users (reviews, comments)</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>3. Legal Basis for Processing (POPIA Requirement)</h2>
          <p>
            Under POPIA, we must have a lawful basis to process your personal information. We collect and process your data based on:
          </p>
          <ul>
            <li><strong>Consent:</strong> You have explicitly consented to the processing (e.g., opt-in to newsletters, vendor applications)</li>
            <li><strong>Contract Performance:</strong> Processing is necessary to fulfill a contract with you (e.g., order processing, vendor agreements)</li>
            <li><strong>Legal Obligation:</strong> We are required by law to process certain information (e.g., tax records, business registration)</li>
            <li><strong>Legitimate Interest:</strong> We have a legitimate business need and your rights don't override it (e.g., fraud prevention, security)</li>
            <li><strong>Public Interest:</strong> Processing serves a public interest or official function</li>
            <li><strong>Vital Interests:</strong> Processing is necessary to protect vital interests</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>4. How Do We Use Your Information?</h2>
          <p>
            We use the information we collect for various purposes:
          </p>
          <ul>
            <li><strong>Service Provision:</strong> Processing orders, managing your account, and providing customer support</li>
            <li><strong>Payment Processing:</strong> Facilitating transactions with payment processors like Stripe and PayPal</li>
            <li><strong>Vendor Management:</strong> Reviewing vendor applications, managing vendor accounts, and facilitating vendor-customer communication</li>
            <li><strong>Communication:</strong> Sending transactional emails, order confirmations, and customer service responses</li>
            <li><strong>Marketing:</strong> Sending promotional emails and marketing communications (with your consent)</li>
            <li><strong>Analytics:</strong> Analyzing website usage patterns to improve our services and user experience</li>
            <li><strong>Fraud Prevention:</strong> Detecting and preventing fraudulent activities, unauthorized access, and security breaches</li>
            <li><strong>Legal Compliance:</strong> Complying with legal obligations, court orders, and government requests</li>
            <li><strong>Customer Service:</strong> Responding to inquiries, resolving disputes, and improving service quality</li>
            <li><strong>Personalization:</strong> Customizing your experience and providing personalized recommendations</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>5. How Do We Share Your Information?</h2>
          
          <div className="privacy-subsection">
            <h3>5.1 Information Shared with Vendors</h3>
            <p>
              When you purchase a product, we share your order information (name, email, address, and order details) 
              with the relevant vendor to fulfill your order. Vendors are contractually bound to use this information 
              solely for order fulfillment and customer service.
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>5.2 Service Providers</h3>
            <p>
              We share your information with third-party service providers who assist us in operating our website 
              and providing services:
            </p>
            <ul>
              <li><strong>Payment Processors:</strong> Stripe and PayPal (for payment processing)</li>
              <li><strong>Email Service Providers:</strong> For sending transactional and marketing emails</li>
              <li><strong>Cloud Storage Providers:</strong> For secure data storage</li>
              <li><strong>Shipping Partners:</strong> For delivery of purchased products</li>
              <li><strong>Analytics Providers:</strong> Google Analytics and similar tools</li>
              <li><strong>Customer Support Tools:</strong> CRM and ticketing systems</li>
            </ul>
            <p>
              These service providers are contractually obligated to use your information only as necessary to provide 
              services and must comply with this Privacy Policy and applicable privacy laws.
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>5.3 Legal Requirements</h3>
            <p>
              We may disclose your information when required by law, including:
            </p>
            <ul>
              <li>Court orders or legal process</li>
              <li>Government or regulatory agency requests</li>
              <li>Enforcement of our Terms & Conditions</li>
              <li>Protection of our rights, privacy, safety, or property</li>
              <li>Detection and prevention of fraud or security issues</li>
            </ul>
          </div>

          <div className="privacy-subsection">
            <h3>5.4 Business Transfers</h3>
            <p>
              If we are involved in a merger, acquisition, bankruptcy, dissolution, reorganization, or similar transaction, 
              your information may be transferred as part of that transaction. We will notify you of any such change and 
              any choices you may have regarding your information.
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>5.5 Non-Personal Information</h3>
            <p>
              We may share aggregated, anonymized, or non-personally identifiable information with third parties for 
              marketing, advertising, analytics, and other purposes without restriction.
            </p>
          </div>
        </section>

        <section className="policy-section">
          <h2>6. Your Rights Under POPIA</h2>
          <p>
            Under the Protection of Personal Information Act, 2013, you have the following rights:
          </p>
          
          <div className="privacy-subsection">
            <h3>6.1 Right to Access</h3>
            <p>
              You have the right to request access to the personal information we hold about you. 
              You can request this information free of charge (unless your request is manifestly unfounded or excessive).
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>6.2 Right to Correction</h3>
            <p>
              If your personal information is inaccurate, incomplete, or misleading, you have the right to request 
              that we correct, update, or complete it.
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>6.3 Right to Deletion</h3>
            <p>
              You may request that we delete your personal information in certain circumstances, including:
            </p>
            <ul>
              <li>The information is no longer necessary for the purpose it was collected</li>
              <li>You withdraw consent and there is no other lawful basis for processing</li>
              <li>You object to processing and there is no overriding legitimate interest</li>
              <li>The information was unlawfully processed</li>
              <li>Deletion is required to comply with a legal obligation</li>
            </ul>
            <p>
              Note: We may retain certain information as required by law or for legitimate business purposes 
              (e.g., fraud prevention, financial records).
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>6.4 Right to Object</h3>
            <p>
              You have the right to object to the processing of your personal information for direct marketing purposes. 
              If you object, we will cease such processing immediately.
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>6.5 Right to Data Portability</h3>
            <p>
              You have the right to request your personal information in a structured, commonly used, and machine-readable format, 
              and to transmit that information to another controller.
            </p>
          </div>

          <div className="privacy-subsection">
            <h3>6.6 Right to Lodge a Complaint</h3>
            <p>
              If you believe we have violated your rights under POPIA, you have the right to lodge a complaint with 
              the Information Regulator of South Africa.
            </p>
            <p>
              <strong>Information Regulator of South Africa</strong>
            </p>
            <ul>
              <li><strong>Website:</strong> www.inforegulator.org.za</li>
              <li><strong>Email:</strong> complaints@inforegulator.org.za</li>
              <li><strong>Postal Address:</strong> JD House, 27 Stiemens Street, Braamfontein, Johannesburg 2001</li>
              <li><strong>Telephone:</strong> +27 (0) 10 023 5200</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>7. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction, including:
          </p>
          <ul>
            <li>SSL/TLS encryption for data in transit</li>
            <li>Encrypted storage for sensitive data</li>
            <li>Secure password hashing for user credentials</li>
            <li>Regular security audits and updates</li>
            <li>Restricted access to personal information (need-to-know basis)</li>
            <li>Secure deletion of data when no longer needed</li>
            <li>Incident response procedures for data breaches</li>
          </ul>
          <p>
            However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, 
            we cannot guarantee absolute security. You use our website at your own risk.
          </p>
        </section>

        <section className="policy-section">
          <h2>8. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, 
            or as required by law:
          </p>
          <ul>
            <li><strong>Account Information:</strong> Retained while your account is active and for 3 years after closure</li>
            <li><strong>Transaction Records:</strong> Retained for 6 years for tax and audit purposes</li>
            <li><strong>Vendor Applications:</strong> Retained for 2 years after decision (approval/rejection)</li>
            <li><strong>Marketing Communications:</strong> Retained until you unsubscribe</li>
            <li><strong>Cookies:</strong> Retained for the duration specified in our Cookie Policy</li>
            <li><strong>Support Records:</strong> Retained for 2 years after resolution</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>9. International Data Transfers</h2>
          <p>
            Some of our service providers may be located outside of South Africa. When we transfer your personal information 
            internationally, we ensure:
          </p>
          <ul>
            <li>Appropriate safeguards are in place (e.g., standard contractual clauses)</li>
            <li>The receiving country provides adequate protection</li>
            <li>You have explicitly consented to the transfer</li>
          </ul>
          <p>
            By using our website, you consent to the transfer of your information as described in this policy.
          </p>
        </section>

        <section className="policy-section">
          <h2>10. Children's Privacy</h2>
          <p>
            Our website is not intended for children under 18 years of age. We do not knowingly collect personal information 
            from children under 18. If we become aware that we have collected information from a child under 18, 
            we will delete such information promptly.
          </p>
          <p>
            If you believe we have collected information from a child under 18, please contact us immediately.
          </p>
        </section>

        <section className="policy-section">
          <h2>11. Opting Out of Marketing Communications</h2>
          <p>
            We only send marketing communications to users who have opted in or have an existing business relationship with us. 
            You can unsubscribe from marketing communications at any time by:
          </p>
          <ul>
            <li>Clicking the "Unsubscribe" link in any marketing email</li>
            <li>Updating your preferences in your account settings</li>
            <li>Contacting us at the email address below</li>
          </ul>
          <p>
            Note: Even if you opt out of marketing communications, we may still send you transactional emails 
            (e.g., order confirmations, account notices).
          </p>
        </section>

        <section className="policy-section">
          <h2>12. Third-Party Links and Services</h2>
          <p>
            Our website may contain links to third-party websites and services. This Privacy Policy does not apply to 
            these third-party sites. We encourage you to review the privacy policies of any third-party sites before 
            providing your personal information.
          </p>
          <p>
            We are not responsible for the privacy practices of third-party sites or services.
          </p>
        </section>

        <section className="policy-section">
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
            legal requirements, or other factors. We will notify you of material changes by:
          </p>
          <ul>
            <li>Updating the "Last Updated" date at the top of this policy</li>
            <li>Posting a prominent notice on our website</li>
            <li>Sending you an email notification (if required)</li>
          </ul>
          <p>
            Your continued use of our website following the posting of changes constitutes your acceptance of such changes.
          </p>
        </section>

        <section className="policy-section">
          <h2>14. Data Protection Officer & POPIA Compliance Officer</h2>
          <p>
            We have appointed a Data Protection Officer to oversee our compliance with POPIA and other privacy laws. 
            If you have questions about our privacy practices or wish to exercise your POPIA rights, you may contact our 
            Data Protection Officer:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:mashaba.enterprise2025@gmail.com">mashaba.enterprise2025@gmail.com</a></p>
            <p><strong>Response Time:</strong> We aim to respond to requests within 30 days</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>15. How to Exercise Your Rights</h2>
          <p>
            To exercise any of your rights under POPIA (access, correction, deletion, objection, data portability), 
            please submit a written request to our Data Protection Officer at the contact information above. Include:
          </p>
          <ul>
            <li>Your full name and contact information</li>
            <li>Clear description of the request</li>
            <li>The right you wish to exercise</li>
            <li>Any supporting documentation</li>
          </ul>
          <p>
            We will respond to your request within 30 days. Requests may be refused in limited circumstances as permitted by law.
          </p>
        </section>

        <section className="policy-section">
          <h2>16. Vendor Applicants - Special Considerations</h2>
          <p>
            If you are applying to become a vendor, we collect and process additional personal information:
          </p>
          <div className="privacy-subsection">
            <h3>16.1 Additional Information Collected</h3>
            <ul>
              <li>Business registration documents</li>
              <li>Tax identification numbers</li>
              <li>Bank account information</li>
              <li>Proof of identity</li>
              <li>Product information and images</li>
            </ul>
          </div>

          <div className="privacy-subsection">
            <h3>16.2 Use of Vendor Information</h3>
            <ul>
              <li>Verifying business legitimacy and authenticity</li>
              <li>Conducting background checks (as permitted by law)</li>
              <li>Processing payments for orders</li>
              <li>Managing vendor accounts and communications</li>
              <li>Compliance with tax and financial regulations</li>
              <li>Dispute resolution and fraud prevention</li>
            </ul>
          </div>

          <div className="privacy-subsection">
            <h3>16.3 Vendor Consent</h3>
            <p>
              By submitting a vendor application, you explicitly consent to the collection, processing, and use of your 
              personal information as described in this Privacy Policy and our Terms & Conditions.
            </p>
          </div>
        </section>

        <section className="policy-section">
          <h2>17. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, 
            please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:mashaba.enterprise2025@gmail.com">mashaba.enterprise2025@gmail.com</a></p>
            <div className="website-logo">
              <img src="/logo.png" alt="Heritage Hub Logo" className="logo-image" />
            </div>
            <p><strong>Country:</strong> South Africa</p>
            <p><strong>Applicable Law:</strong> Protection of Personal Information Act, 2013 (POPIA)</p>
          </div>
        </section>

        <div className="popia-compliance-notice">
          <h3>📋 POPIA Compliance Statement</h3>
          <p>
            This organization is committed to compliance with the Protection of Personal Information Act, 2013 (POPIA) 
            and the principles contained therein. We take your privacy seriously and have implemented appropriate 
            safeguards to protect your personal information. If you have any concerns about our privacy practices, 
            please contact our Data Protection Officer or lodge a complaint with the Information Regulator of South Africa.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
